<div class="ai-summary">
    <!-- setting up a summary: open a saved one or start from a prompt, name it, and say what to write -->
    <section class="card setup" aria-labelledby="ai-setup-title">
        <header class="card-head">
            <div>
                <h3 id="ai-setup-title">Write a summary with AI</h3>
                <p>Open a saved summary, or start from a ready-made prompt and change it to suit.</p>
            </div>
            <button mat-stroked-button (click)="onNew()" matTooltip="Start a new summary, clearing the fields">
                <mat-icon>add</mat-icon>New summary
            </button>
        </header>

        <div class="pickers">
            <mat-form-field subscriptSizing="dynamic">
                <mat-label>Saved summaries</mat-label>
                <mat-icon matPrefix>history</mat-icon>
                <mat-select [(value)]="summary" (selectionChange)="onSummaryChange($event.value)" [placeholder]="summaries.length ? 'Open a saved summary' : 'None saved yet'" [disabled]="!summaries.length">
                    @for (s of summaries; track s) {
                        <mat-option [value]="s">{{ s.name }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>
            <mat-form-field subscriptSizing="dynamic">
                <mat-label>Start from a prompt</mat-label>
                <mat-icon matPrefix>auto_awesome</mat-icon>
                <mat-select [(value)]="selectedPrompt" (selectionChange)="onPromptChange($event.value)" placeholder="Choose a ready-made prompt">
                    @for (p of prompts; track p) {
                        <mat-option [value]="p">{{ p.name }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>
        </div>

        @if (summary) {
            <div class="details">
                <mat-form-field class="name" subscriptSizing="dynamic">
                    <mat-label>Summary name</mat-label>
                    <input matInput [(ngModel)]="summary.name" type="text" required placeholder="For example Executive briefing, March" (ngModelChange)="setDescription()">
                </mat-form-field>
                @if (linqset.isProjectModel()) {
                    <mat-slide-toggle [(ngModel)]="linqset.filter.comparison">Compare with the current state</mat-slide-toggle>
                }
            </div>

            <mat-form-field class="prompt" subscriptSizing="dynamic">
                <mat-label>What should the summary cover?</mat-label>
                <textarea matInput [(ngModel)]="summary.prompt" cdkTextareaAutosize cdkAutosizeMinRows="4" cdkAutosizeMaxRows="14"
                    placeholder="Describe what you want, for example: summarise where time and money go, and the three biggest risks"></textarea>
                <mat-hint>{{ promptHint() }}</mat-hint>
            </mat-form-field>

            <footer class="card-foot">
                <p class="note"><mat-icon>info</mat-icon><span>{{ description }}</span></p>
                <div class="actions">
                    @if (summary.created && canChange()) {
                        <button mat-button color="warn" (click)="onDelete()" matTooltip="Delete this saved summary">
                            <mat-icon>delete</mat-icon>Delete
                        </button>
                    }
                    @if (canChange()) {
                        <button mat-stroked-button (click)="onSave()" [disabled]="!canSave()" [matTooltip]="canSave() ? 'Save this summary to come back to' : 'Name the summary and generate it before saving'">
                            <mat-icon>save</mat-icon>Save
                        </button>
                    }
                    <button mat-flat-button color="accent" (click)="onGenerate()" [disabled]="!canGenerate() || loading" matTooltip="Write the summary from the model data and the prompt" data-testid="ai-generate">
                        <mat-icon>auto_awesome</mat-icon>{{ hasReport() ? 'Generate again' : 'Generate summary' }}
                    </button>
                </div>
            </footer>
        }
    </section>

    <!-- the summary itself -->
    @if (summary) {
        <section class="card report" aria-labelledby="ai-report-title" aria-live="polite">
            <header class="card-head">
                <div>
                    <h3 id="ai-report-title">{{ summary.name || 'Summary' }}</h3>
                    @if (hasReport() && !loading) {
                        <span class="status" [class.outdated]="outdated()">
                            <mat-icon>{{ outdated() ? 'update' : 'check_circle' }}</mat-icon>{{ message }}
                        </span>
                    }
                </div>
                @if (hasReport() && !loading) {
                    <div class="report-tools">
                        <mat-button-toggle-group [(ngModel)]="format" aria-label="Show the summary formatted or as plain text" hideSingleSelectionIndicator>
                            <mat-button-toggle value="html">Formatted</mat-button-toggle>
                            <mat-button-toggle value="text">Plain text</mat-button-toggle>
                        </mat-button-toggle-group>
                        <button mat-stroked-button (click)="onExport()" matTooltip="Download the summary">
                            <mat-icon>download</mat-icon>Export
                        </button>
                    </div>
                }
            </header>

            @if (loading) {
                <div class="state" role="status">
                    <mat-spinner diameter="32"></mat-spinner>
                    <p>Writing the summary… this can take a minute.</p>
                </div>
            } @else if (hasReport()) {
                <p class="disclaimer"><mat-icon>science</mat-icon>Written by AI (beta). LINQ can't guarantee it's accurate, so check it before you rely on it.</p>
                @if (format === 'html') {
                    <article class="report-body" [innerHtml]="safeHtml"></article>
                } @else {
                    <article class="report-body plain">{{ summary.htmlSummary }}</article>
                }
            } @else {
                <div class="state empty">
                    <mat-icon>description</mat-icon>
                    <p>Choose a prompt or write your own, then generate the summary. It will appear here.</p>
                </div>
            }
        </section>
    }
</div>
